import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Store } from '../../../types';
import { useSharedStores } from '../hooks';

export interface FranchiseOption {
  code: string;
  name: string;
  storeCount: number;
}

/**
 * Utility functions to convert between FranchiseOption and FranchiseGroup.
 */
/* const franchiseGroupToOption = (group: FranchiseGroup): FranchiseOption => ({
  code: group.id,
  name: group.name,
  storeCount: group.storeCount,
});

const franchiseOptionToGroup = (option: FranchiseOption): FranchiseGroup => ({
  id: option.code,
  name: option.name,
  storeCount: option.storeCount,
}); */

/**
 * Base props shared by single and multiple selectors.
 */
interface BaseFranchiseSelectorProps {
  label: string;
  placeholder: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Props for single selection mode.
 */
export interface SingleFranchiseSelectorProps extends BaseFranchiseSelectorProps {
  multiple?: false;
  value?: FranchiseOption | null;
  onChange: (value: FranchiseOption | null, stores: Store[]) => void;
}

/**
 * Props for multiple selection mode.
 */
export interface MultipleFranchiseSelectorProps extends BaseFranchiseSelectorProps {
  multiple: true;
  value?: FranchiseOption[];
  onChange: (value: FranchiseOption[], stores: Store[]) => void;
}

/** Union type for component props */
type FranchiseSelectorProps = SingleFranchiseSelectorProps | MultipleFranchiseSelectorProps;

/** Type guard to check for multiple selection props */
function isMultipleProps(
  props: FranchiseSelectorProps
): props is MultipleFranchiseSelectorProps {
  return props.multiple === true;
}

const FranchiseSelector: React.FC<FranchiseSelectorProps> = (props) => {
  const { label, placeholder, disabled = false, 'data-testid': dataTestId } = props;
  const multiple = isMultipleProps(props);
  const value = multiple ? props.value || [] : props.value || null;

  // Use shared stores cache instead of making individual API calls
  const { allStores, isLoading, error: apiError } = useSharedStores();

  // Local state for input text in the autocomplete
  const [inputValue, setInputValue] = useState('');

  // Convert API error to a display string, if needed.
  const error = apiError ? 'Failed to load stores from API' : null;

  // Ensure an initial empty value for multiple selection.
  useEffect(() => {
    if (multiple && props.value === undefined) {
      props.onChange([], []);
    }
  }, [multiple, props.value, props.onChange]);

  // Group the loaded stores by franchise code.
  const franchiseOptions = useMemo(() => {
    if (!allStores) return [];
    const franchiseMap = new Map<string, { stores: Store[]; name: string }>();

    allStores.forEach((store) => {
      const code = store.franchiseCode || '';
      if (!franchiseMap.has(code)) {
        // Fallback name for stores with no defined franchise code.
        const defaultName = code ? code : 'Unassigned Region';
        franchiseMap.set(code, {
          stores: [],
          name: defaultName,
        });
      }
      franchiseMap.get(code)!.stores.push(store);
    });

    // Create options from the franchise map
    const options = Array.from(franchiseMap.entries()).map(([code, data]) => ({
      code,
      name: data.name,
      storeCount: data.stores.length,
    }));

    // Add "All" option at the beginning of the list
    if (allStores.length > 0) {
      options.unshift({
        code: 'all',
        name: 'All Franchises',
        storeCount: allStores.length,
      });
    }

    return options;
  }, [allStores]);

  // Filter options based on search input and (for multiple selection) remove already selected options.
  const filteredOptions = useMemo(() => {
    const searchText = inputValue.trim().toLowerCase();
    const options = searchText === ''
      ? franchiseOptions
      : franchiseOptions.filter((option) =>
          option.code.toLowerCase().includes(searchText) ||
          option.name.toLowerCase().includes(searchText)
        );

    return multiple && Array.isArray(value)
      ? options.filter(
          (option) =>
            !value.some((selected) => selected.code === option.code)
        )
      : options;
  }, [franchiseOptions, inputValue, multiple, value]);

  /**
   * Retrieve all stores corresponding to the selected franchise options.
   */
  const getStoresForFranchises = useCallback(
    (selectedOptions: FranchiseOption | FranchiseOption[] | null): Store[] => {
      if (!allStores || !selectedOptions) return [];
      
      // Handle array of options (multiple selection)
      if (Array.isArray(selectedOptions)) {
        // Check if "All" option is included
        if (selectedOptions.some(option => option.code === 'all')) {
          return allStores;
        }
        
        const codes = selectedOptions.map((option) => option.code);
        return allStores.filter((store) => {
          const storeCode = store.franchiseCode || '';
          return codes.includes(storeCode);
        });
      } 
      // Handle single option
      else {
        // Return all stores if "All" option is selected
        if (selectedOptions.code === 'all') {
          return allStores;
        }
        
        return allStores.filter((store) => {
          const storeCode = store.franchiseCode || '';
          return storeCode === selectedOptions.code;
        });
      }
    },
    [allStores]
  );

  // Handler for selection changes
  const handleChange = useCallback(
    (newValue: FranchiseOption | FranchiseOption[] | null) => {
      const selectedStores = getStoresForFranchises(newValue);
      if (multiple) {
        props.onChange(newValue as FranchiseOption[], selectedStores);
      } else {
        (props as SingleFranchiseSelectorProps).onChange(newValue as FranchiseOption | null, selectedStores);
      }
    },
    [getStoresForFranchises, multiple, props]
  );

  return (
    <Box>
      <Autocomplete
        multiple={multiple}
        options={filteredOptions}
        value={value}
        onChange={(_, newValue) => handleChange(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        data-testid={dataTestId}
        getOptionLabel={(option) => `${option.name} (${option.storeCount} stores)`}
        isOptionEqualToValue={(option, val) => option.code === val.code}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Chip
                label={`${option.name} (${option.storeCount})`}
                {...tagProps}
              />
            );
          })
        }
        renderOption={(optionProps, option) => (
          <li {...optionProps}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.storeCount} stores {option.code ? `• Code: ${option.code}` : '• No code'}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            error={!!error}
            helperText={error ? 'Error loading franchise groups' : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        loading={isLoading}
        loadingText="Loading franchise groups..."
        noOptionsText="No franchise groups found"
        disabled={disabled}
      />
      {error && (
        <Typography color="error" variant="caption">
          Failed to load franchise groups. Please try again.
        </Typography>
      )}
    </Box>
  );
};

export default FranchiseSelector;
